/**
 * The geometry rules behind the labeled line under a headline tile (Time to
 * complete, Spend) — PURE, so they are pinned by `headlineTrend.test.ts`
 * without drawing anything. The phone draws the same chart from the same
 * rules (`dashboard_logic.dart`); change one, change both.
 */

/** Which of the `n` monthly points carry an x-axis label: first, last and three between. */
export function tickIndices(n: number): number[] {
  if (n <= 0) return [];
  if (n <= 5) return Array.from({ length: n }, (_, i) => i);
  const last = n - 1;
  return [...new Set([0, Math.round(last / 4), Math.round(last / 2), Math.round((last * 3) / 4), last])].sort((a, b) => a - b);
}

/**
 * The y-range: every known value AND the benchmark (so the benchmark line is
 * always on the chart and a flat series never stretches to fill the box),
 * padded, and never below zero when nothing is negative.
 */
export function trendRange(values: (number | null)[], benchmark: number | null): { lo: number; hi: number } {
  const all = [...values.filter((v): v is number => v != null), ...(benchmark != null ? [benchmark] : [])];
  if (all.length === 0) return { lo: 0, hi: 1 };
  let lo = Math.min(...all);
  let hi = Math.max(...all);
  const nonNegative = lo >= 0;
  const span = hi - lo;
  const pad = span === 0 ? (hi === 0 ? 1 : Math.abs(hi) * 0.1) : span * 0.12;
  lo -= pad;
  hi += pad;
  if (nonNegative && lo < 0) lo = 0;
  return { lo, hi };
}

/** Index of the highest known value — null with fewer than two known points; the first of equal highs. */
export function peakIndex(values: (number | null)[]): number | null {
  if (values.filter((v) => v != null).length < 2) return null;
  let best: number | null = null;
  values.forEach((v, i) => {
    if (v != null && (best == null || v > (values[best] as number))) best = i;
  });
  return best;
}

/** Index of the first known value, or null. With `lastIndex` it bounds the span the chart draws edge to edge. */
export function firstIndex(values: (number | null)[]): number | null {
  for (let i = 0; i < values.length; i++) if (values[i] != null) return i;
  return null;
}

/** Index of the last known value (the "now" point), or null. */
export function lastIndex(values: (number | null)[]): number | null {
  for (let i = values.length - 1; i >= 0; i--) if (values[i] != null) return i;
  return null;
}

/** Runs of consecutive known months — a month with no data breaks the line. */
export function runsOf(values: (number | null)[]): number[][] {
  const runs: number[][] = [];
  let cur: number[] = [];
  values.forEach((v, i) => {
    if (v == null) {
      if (cur.length) runs.push(cur);
      cur = [];
    } else cur.push(i);
  });
  if (cur.length) runs.push(cur);
  return runs;
}

/**
 * The industry spend line. The reference is dollars per unit per month; the
 * chart plots the building's monthly TOTAL, so the line is the reference ×
 * the live units. Null with no units — there is nothing honest to draw.
 */
export const spendBenchmarkTotal = (perUnitMonth: number, liveUnits: number): number | null =>
  liveUnits > 0 ? perUnitMonth * liveUnits : null;
